import React, { useState } from 'react'
import { Box, Button, Stack, TextField, Typography, Tooltip, IconButton, Checkbox, FormControlLabel } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { CorporateCodePost } from '../../../redux/corporateCodeService'
import { useAppDispatch } from '../../../redux/storeExports'
import { useNavigate } from 'react-router-dom'
import { fonts } from '../../../config/SigninTheme'
import MpTheme from '../../../config/MpTheme'
import { InvoiceInfo as InvoiceInfoType } from '../../../redux/CorporateInvoice/CorporateInvoiceTypes'
import { generateManualInvoice } from '../../../redux/CorporateInvoice/CorporateInvoice'
import { Toast } from '../../toast/toast';

export type CorporateFormProps = {
  submitLabel: string
  corporateCode: CorporateCodePost
  invoiceInfo: InvoiceInfoType | null
}

export const GenerateInvoiceForm = ({
  submitLabel,
  corporateCode,
  invoiceInfo,
}: CorporateFormProps) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [errors, setErrors] = useState<{ pendingInvoiceAmount?: string; currentInvoiceAmount?: string }>({});
  const [loading, setLoading] = useState(false)

  const [formData, setFormData] = useState({
    pendingInvoiceAmount: invoiceInfo?.pending_invoice?.amount?.toString() || '',
    currentInvoiceAmount: '',
    includePending: true,
  })

  const handleChange = (field: keyof typeof formData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))

     if (typeof value === 'string') {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

 const validate = () => {
    let valid = true
    const newErrors: typeof errors = {}

    // At least one field should have value
    if (
      formData.pendingInvoiceAmount.trim() === '' &&
      formData.currentInvoiceAmount.trim() === ''
    ) {
      newErrors.pendingInvoiceAmount = 'At least one amount is required'
      newErrors.currentInvoiceAmount = 'At least one amount is required'
      valid = false
    }

    const validateAmount = (field: keyof typeof formData, val: string) => {
      if (!val) return
      const amount = Number(val)
      if (isNaN(amount) || amount <= 0) {
        newErrors[field as keyof typeof errors] = 'Amount must be a positive number'
        valid = false
      } else if (amount > 99999999) {
        newErrors[field as keyof typeof errors] = 'Amount cannot exceed 99,999,999'
        valid = false
      }
    }

    validateAmount('pendingInvoiceAmount', formData.pendingInvoiceAmount)
    validateAmount('currentInvoiceAmount', formData.currentInvoiceAmount)

    setErrors(newErrors)
    return valid
  }
  const handleSubmit = async () => {
    if (!validate()) return

    const payload = {
      pending: invoiceInfo?.pending_invoice
        ? {
            invoice_id: invoiceInfo.pending_invoice.invoice_id,
            amount: formData.includePending
            ? Number(formData.pendingInvoiceAmount) 
            : 0
          }
        : null,
      current: invoiceInfo?.unpaid_invoice
      ?{
        invoice_id: invoiceInfo?.unpaid_invoice?.invoice_id || '',
        amount: Number(formData.currentInvoiceAmount),
      } : null,
    }

    try {
      setLoading(true)
      await dispatch(generateManualInvoice({ code: corporateCode.code, payload })).unwrap()
      Toast.show({
        type: 'success',
        text1: 'Manual invoice generated successfully!',
      })
      setTimeout(() => {
        navigate('/home/manage');
      }, 1500)
    } catch (error: any) {
      Toast.show({
        type: 'error',
        text1: 'Failed to generate manual invoice',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Stack
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxWidth: MpTheme.layouts.widths.md,
        width: '100%',
        mx: 'auto',
      }}
    >
      <Stack spacing={5}>
        <Box>
          <Typography variant="h2">Code</Typography>
          <TextField
            name="code"
            placeholder="Code"
            required
            fullWidth
            disabled
            value={corporateCode.code}
          />
        </Box>
        {invoiceInfo?.pending_invoice || invoiceInfo?.unpaid_invoice ?
        (
        <>
        {/* Pending Invoice */}
        {invoiceInfo?.pending_invoice && (
        <Box>
             <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h2">Pending Invoice Amount</Typography>
              <Tooltip
                title="If you want to adjust the existing pending invoice amount, update it here."
                arrow
                placement="right"
                slotProps={{
                  tooltip: {
                    sx: { fontSize: '14px', color: '#fff' }
                  }
                }}
              >
                <IconButton size="small">
                  <InfoOutlinedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
              </Stack>
        
          <TextField
            name="pendingInvoiceAmount"
            placeholder="Enter Pending Invoice Amount"
            fullWidth
            inputProps={{ min: 0 }}
            type="number"
            value={formData.pendingInvoiceAmount}
            onChange={(e) => handleChange('pendingInvoiceAmount', e.target.value)}
            error={!!errors.pendingInvoiceAmount}
            helperText={errors.pendingInvoiceAmount}
          />
          {invoiceInfo.unpaid_invoice && (
          <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.includePending}
                  onChange={(e) => handleChange('includePending', e.target.checked)}
                />
              }
              label="Include pending invoice"
            />
            )}
        </Box>
        )}
        {/* New invoice */}
        {invoiceInfo?.unpaid_invoice && (
          <>
        <Box>
              <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h2">Billable User(s)</Typography>
              <Tooltip
                title="This shows the number of users included in the new invoice."
                arrow
                placement="right"
                slotProps={{
                  tooltip: {
                    sx: { fontSize: '14px', color: '#fff' }
                  }
                }}
              >
                <IconButton size="small">
                  <InfoOutlinedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Stack>
            <TextField
              name="current_invoice_users"
              placeholder="Users"
              fullWidth
              disabled
              value={invoiceInfo.unpaid_invoice?.user_count}
            />
        </Box>
        <Box>
          <Typography variant="h2">Current Invoice Amount</Typography>
          <TextField
            name="currentInvoiceAmount"
            placeholder="Enter Current Invoice Amount"
            required
            fullWidth
            inputProps={{ min: 0 }}
            type="number"
            value={formData.currentInvoiceAmount}
            onChange={(e) => handleChange('currentInvoiceAmount', e.target.value)}
            error={!!errors.currentInvoiceAmount}
            helperText={errors.currentInvoiceAmount}
          />
        </Box>
        </>
        )}
      </>
      ):
     <Box sx={{ textAlign: 'center', mt: 2 }}>       
        <Box sx={{ mt: 2 }}>
          <Typography variant="h2" sx={{ textAlign: 'left' }}>
            Billable User(s)
          </Typography>
          <TextField
            name="billable_users"
            placeholder="Users"
            fullWidth
            disabled
            value={0}
          />
        </Box>
         <Typography color="error" variant="body1" sx={{ mt: 3, textAlign: 'left' }}>
           Not eligible for invoice generation as no billable users are available
        </Typography>

      </Box>
      }
      </Stack>

      <Box sx={{ mt: 5 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          color="darkPurple"
          disabled={(!formData.currentInvoiceAmount && !formData.pendingInvoiceAmount) || loading}
          sx={{
            background: '#426BF0',
            width: '100%',
            '&.Mui-disabled': {
              background: '#ccc',
              fontFamily: fonts.Inter.SemiBold,
              color: '#ffffff',
            },
          }}
        >
          {submitLabel}
        </Button>
      </Box>
    </Stack>
  )
}
